import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Button } from "react-bootstrap";

import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./style.css";

import CondtionalDropdown from "../conditional-dropdown";
import SegmentedControl from "../segmented-control";

import {
    mine as myCalendars,
    firm as firmCalendars,
    list as listCalendars,
    available as sharedCalendars
} from "../../actions/calendar";

import {
    save as saveEvent,
    destroy as deleteEvent,
    send as sendEvent,
    mapped as mappedEvent,
    list as listEvents
} from "../../actions/calendar.event";

import { byPerson as rolePeopleByPerson, mine as myRolePeople } from "../../actions/role.people";
import {
    get as getAvailability,
    save as saveAvailability,
    destroy as deleteAvailability,
    copy as copyAvailability,
} from "../../actions/role.availability";
import { get as getSetting } from "../../actions/settings";

import { isGuid } from "../../utils/string";
import { normalizeCalendarDescription } from "../../utils/calendarDescription";
import {
    BOARD_TASK_MODES,
    BOARD_TASK_MODE_OPTIONS,
    boardTaskCalendarColorKey,
    boardTaskEventMatchesMode,
    calendarLabel,
    currentPersonUuid,
    isBoardTaskEvent,
    isBoardTasksCalendar
} from "../../utils/boardTasksCalendar";
import EventModal from "./modals/event";
import ConfirmModal from "./modals/confirm";
import AvailabilityModal from "./modals/availability";
import AvailabilityCopyModal from "./modals/availability.copy";

import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(Calendar);
const HISTORY_VIEW = "history";
const HISTORY_INITIAL_LIMIT = 20;
const HISTORY_STEP = 10;
const CALENDAR_SCOPE_MODES = {
    ALL: "all",
    CONTEXT: "context",
};

const formatHistoryDate = (date) => {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return "";
    return format(value, "MMM d, yyyy h:mm a");
};

const HistoryView = ({
    events = [],
    onSelectEvent,
    onDoubleClickEvent,
    historyHasMore,
    historyLoading,
    onHistoryLoadMore,
}) => {
    const sorted = [...events].sort((a, b) => (b.start?.getTime?.() || 0) - (a.start?.getTime?.() || 0));

    return (
        <div className="rbc-agenda-view" style={{ height: "100%", overflowY: "auto" }}>
            <table className="rbc-agenda-table table table-hover mb-0">
                <thead>
                    <tr>
                        <th className="text-muted text-small text-uppercase">When</th>
                        <th className="text-muted text-small text-uppercase">Event</th>
                        <th className="text-muted text-small text-uppercase">Calendar</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.length ? sorted.map((event) => (
                        <tr
                            key={event.id || event.uuid}
                            role="button"
                            onClick={() => onSelectEvent?.(event)}
                            onDoubleClick={() => onDoubleClickEvent?.(event)}
                        >
                            <td style={{ whiteSpace: "nowrap" }}>
                                <div>{formatHistoryDate(event.start)}</div>
                                {event.allDay ? <div className="text-muted small">All day</div> : null}
                            </td>
                            <td>
                                <div className="fw-semibold">{event.title || "(untitled)"}</div>
                                {event.description ? (
                                    <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
                                        {event.description}
                                    </div>
                                ) : null}
                            </td>
                            <td>{calendarLabel(event.calendar) || event.calendar?.name || ""}</td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={3} className="text-muted text-center p-4">
                                No previous events.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {historyHasMore ? (
                <div className="d-flex justify-content-center p-3">
                    <Button
                        type="button"
                        variant="outline-primary"
                        size="sm"
                        disabled={historyLoading}
                        onClick={onHistoryLoadMore}
                    >
                        {historyLoading ? "Loading" : "Load More"}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

HistoryView.range = (date) => ({ start: date, end: date });
HistoryView.navigate = (date) => date;
HistoryView.title = () => "History";

class Display extends React.Component {
    constructor(props) {
        super(props);

        const now = new Date();
        const start = startOfWeek(now, { weekStartsOn: 0 });
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);

        this.state = {
            view: Views.WEEK,
            date: now,
            visible: { start, end },

            calendars: [...(props?.calendars ?? [])],
            visibleCalendarUuids: null,
            boardTaskMode: BOARD_TASK_MODES.MY,
            calendarScopeMode: CALENDAR_SCOPE_MODES.ALL,
            eventTypes: null,
            eventNameTemplate: "",

            events: [],
            historyLimit: HISTORY_INITIAL_LIMIT,
            historyHasMore: false,
            historyLoading: false,

            roles: [],
            myRoles: [],
            availability: [],
            availabilityPersonUuid: null,
            showAvailability: false,

            modal: {
                event: {
                    show: false,
                },
                confirm: {
                    show: false,
                },
                availability: {
                    show: false,
                },
                availabilityCopy: {
                    show: false,
                },
            },
        };
    }

    componentDidMount = async () => {
        this.__isMounted = true;
        window.addEventListener("keydown", this.onKeyDown);

        await this.loadEventSettings();
        await this.loadData();
        await this.loadMyRoles();

        if (this.state.showAvailability) {
            await this.loadAvailability();
        }
    };

    componentDidUpdate = async (prevProps) => {
        if (prevProps.firm !== this.props.firm) {
            await this.loadData().catch((err) => console.error("loadData in componentDidUpdate failed:", err));

            if (this.state.showAvailability) {
                await this.loadAvailability().catch((err) => console.error("loadAvailability in componentDidUpdate failed:", err));
            }
        }

        if (prevProps.owner !== this.props.owner && this.props.owner) {
            await this.loadData().catch((err) => console.error("loadData after owner change failed:", err));
            await this.loadMyRoles().catch((err) => console.error("loadMyRoles in componentDidUpdate failed:", err));

            if (this.state.showAvailability) {
                await this.loadAvailability().catch((err) => console.error("loadAvailability after owner change failed:", err));
            }
        }
    };

    componentWillUnmount = () => {
        this.__isMounted = false;
        window.removeEventListener("keydown", this.onKeyDown);
    };

    openAvailabilityCopyModal = ({ personUuid, roleUuid, date }) => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                availabilityCopy: {
                    show: true,
                    personUuid,
                    roleUuid,
                    sourceDate: date,
                    targetDates: [],
                },
            },
        }));
    };

    closeAvailabilityCopyModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                availabilityCopy: {
                    show: false,
                },
            },
        }));
    };

    openEventModal = ({ uuid = null, sourceUuid = null, type = "Event", time = null, ...event } = {}) => {
        this.setState((prev) => ({
            ...prev,
            scrollToTime: time?.start ?? prev.scrollToTime,
            modal: {
                ...prev.modal,
                event: {
                    ...prev.modal.event,
                    ...event,
                    show: true,
                    uuid,
                    sourceUuid,
                    type,
                    ...(time ? { time } : {}),
                },
            },
        }));
    };

    closeEventModal = () => {
        this.setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                event: { ...prev.modal.event, show: false },
            },
        }));
    };

    openAvailabilityModal = ({ uuid = null, roleUuid = null, time = null, meta = null } = {}) => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                availability: {
                    show: true,
                    uuid,
                    personUuid: prev.availabilityPersonUuid || this.props.owner,
                    roleUuid,
                    roles: prev.roles || [],
                    meta,
                    ...(time ? { time } : {}),
                },
            },
        }));
    };

    closeAvailabilityModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                availability: { show: false },
            },
        }));
    };

    onKeyDown = (ev) => {
        const key = ev.key;

        const el = document.activeElement;
        const typing =
            el &&
            (el.tagName === "INPUT" ||
                el.tagName === "TEXTAREA" ||
                el.isContentEditable);
        if (typing) return;

        if (
            this.state.modal?.event?.show ||
            this.state.modal?.confirm?.show ||
            this.state.modal?.availability?.show ||
            this.state.modal?.availabilityCopy?.show
        ) return;

        if (key === "Insert") {
            const sel = this.state.selected;
            if (sel?.kind !== "slot") return;

            const start = sel.data?.start ? new Date(sel.data.start) : null;
            let end = sel.data?.end ? new Date(sel.data.end) : null;
            if (!start) return;
            if (!end || +end === +start) end = new Date(+start + 30 * 60 * 1000);

            ev.preventDefault();

            if (this.state.showAvailability) {
                this.openAvailabilityModal({ uuid: null, time: { start, end } });
                return;
            }

            this.openEventModal({ uuid: null, type: "Event", time: { start, end } });
            return;
        }

        if (key === "Delete") {
            const selected = this.state.selected?.data;
            if (!selected) return;

            if (selected?.kind === "availability" || selected?.uuid) {
                const uuid = selected?.uuid ?? selected?.id ?? null;
                if (!isGuid(uuid)) return;
                ev.preventDefault();
                this.onModalDelete({ uuid, kind: selected?.kind === "availability" ? "availability" : "event" });
                return;
            }

            const uuid = this.state.selected?.kind === "event" ? this.state.selected?.data?.id : null;
            if (!isGuid(uuid)) return;

            ev.preventDefault();
            this.onModalDelete({ uuid, kind: "event" });
        }
    };

    loadMyRoles = async () => {
        let items = [];
        let availabilityPersonUuid = null;

        if (isGuid(this.props.owner)) {
            const assignments = await this.props.rolePeopleByPerson({ personUuid: this.props.owner });
            items = Array.isArray(assignments?.items) ? assignments.items : [];
            availabilityPersonUuid = this.props.owner;
        } else {
            const assignments = await this.props.myRolePeople();
            items = Array.isArray(assignments?.items) ? assignments.items : [];
            availabilityPersonUuid = items?.[0]?.personUuid ?? null;
        }

        this.setState({
            myRoles: items,
            roles: items.map((x) => x.Role).filter(Boolean),
            availabilityPersonUuid,
        });
    };

    loadAvailability = async (range) => {
        const personUuid = this.state.availabilityPersonUuid || this.props.owner;
        if (!isGuid(personUuid)) {
            this.setState({ availability: [] });
            return;
        }

        const { start, end } = range ?? this.state.visible;
        const startDate = format(start, "yyyy-MM-dd");
        const endDate = format(end, "yyyy-MM-dd");

        const assignments = Array.isArray(this.state.myRoles) ? this.state.myRoles : [];
        const perRole = await Promise.all(
            assignments.map(async (assignment) => {
                const roleUuid = assignment?.roleUuid;
                if (!isGuid(roleUuid)) return [];

                const resp = await this.props.getAvailability({
                    personUuid,
                    roleUuid,
                    start: startDate,
                    end: endDate,
                });

                const rows = Array.isArray(resp?.items) ? resp.items : [];

                return rows.map((row) => {
                    const startAt = new Date(`${row.date}T${String(row.startTime).slice(0, 8)}`);
                    const endAt = new Date(`${row.date}T${String(row.endTime).slice(0, 8)}`);

                    return {
                        id: row.uuid,
                        uuid: row.uuid,
                        kind: "availability",
                        title: row?.Role?.caption ?? assignment?.Role?.caption ?? "Availability",
                        start: startAt,
                        end: endAt,
                        allDay: false,
                        roleUuid: row.roleUuid,
                        personUuid: row.personUuid,
                        date: row.date,
                        startTime: row.startTime,
                        endTime: row.endTime,
                        meta: row.meta ?? null,
                    };
                });
            })
        );

        this.setState({ availability: perRole.flat() });
    };

    loadEventSettings = async () => {
        try {
            const [eventTypes, eventNameTemplate] = await Promise.all([
                this.props.getSetting({ authentic: true, name: "EVENT_TYPE" }),
                this.props.getSetting({ authentic: true, name: "EVENT_NAME_TEMPLATE" }),
            ]);

            if (!this.__isMounted) return;

            this.setState({
                eventTypes: eventTypes?.value ?? null,
                eventNameTemplate: eventNameTemplate?.value ?? "",
            });
        } catch (err) {
            console.error("loadEventSettings failed:", err);
        }
    };

    onAvailabilityOk = async (payload) => {
        await this.props.saveAvailability(payload);
        this.closeAvailabilityModal();
        await this.loadAvailability();
    };

    onAvailabilityDelete = async ({ uuid }) => {
        if (!isGuid(uuid)) return;

        await this.props.deleteAvailability({ uuid });
        this.closeAvailabilityModal();
        await this.loadAvailability();
    };

    onAvailabilityCopyOk = async ({ personUuid, roleUuid, sourceDate, targetDates }) => {
        await this.props.copyAvailability({
            personUuid,
            roleUuid,
            sourceDate,
            targetDates,
        });

        this.closeAvailabilityCopyModal();
        await this.loadAvailability();
    };

    onAvailabilityCopy = async ({ personUuid, roleUuid, date }) => {
        this.openAvailabilityCopyModal({ personUuid, roleUuid, date });
    };

    normalizeEventDates = ({ start, end, allDay }) => {
        const s = start instanceof Date ? start : new Date(start);
        let e = end instanceof Date ? end : new Date(end);

        if (
            e &&
            e.getHours() === 0 &&
            e.getMinutes() === 0 &&
            e.getSeconds() === 0 &&
            e.getMilliseconds() === 0 &&
            +e > +s
        ) {
            e = new Date(e.getTime() - 1);
        }

        if (!e || +e <= +s) {
            e = new Date(+s + 30 * 60 * 1000);
        }

        return { start: s, end: e, allDay: !!allDay };
    };

    patchEventTime = async ({ event, start, end, allDay }) => {
        const uuid = event?.id ?? event?.uuid ?? null;
        if (!isGuid(uuid)) return;

        const normalized = this.normalizeEventDates({ start, end, allDay });

        this.setState((prev) => ({
            ...prev,
            events: (prev.events || []).map((ev) =>
                ev.id === uuid
                    ? { ...ev, start: normalized.start, end: normalized.end, allDay: normalized.allDay }
                    : ev
            ),
        }));

        try {
            await this.props.saveEvent({
                uuid,
                sourceUuid: event?.sourceUuid,
                startsAt: normalized.start.toISOString(),
                endsAt: normalized.end.toISOString(),
                allDay: normalized.allDay,
            });
        } catch (err) {
            console.error("patchEventTime failed:", err);
            await this.loadData();
        }
    };

    normalizeEvent = (ev, calendar) => {
        const evStart = ev.startsAt ?? ev.start ?? ev.startAt ?? ev.start_time;
        const evEnd = ev.endsAt ?? ev.end ?? ev.endAt ?? ev.end_time;
        const sourceUuid = ev.db?.sourceUuid ?? ev.sourceUuid ?? ev.uuid ?? ev.id ?? null;
        const uuid = ev.db?.uuid ?? ev.uuid ?? ev.id ?? null;

        return {
            ...ev,
            uuid,
            sourceUuid,
            eventType: ev.db?.eventType ?? ev.eventType ?? null,
            calendarUuid: ev.db?.calendarUuid ?? ev.calendarUuid ?? ev.calendar?.uuid ?? calendar.uuid,
            calendar: ev.db?.calendar || ev.calendar || calendar,
            boardTask: ev.db?.boardTask || ev.boardTask || null,
            id: uuid,
            title: ev.title ?? ev.name ?? ev.summary ?? "(untitled)",
            description: normalizeCalendarDescription(ev.description),
            start: evStart instanceof Date ? evStart : new Date(evStart),
            end: evEnd instanceof Date ? evEnd : new Date(evEnd),
            allDay: !!(ev.allDay ?? ev.all_day),
        };
    };

    hashString = (str) => {
        let h = 0;
        const s = String(str || "");
        for (let i = 0; i < s.length; i++) {
            h = ((h << 5) - h) + s.charCodeAt(i);
            h |= 0;
        }
        return Math.abs(h);
    };

    calendarColor = (calendarUuid) => {
        const h = this.hashString(calendarUuid);
        const hue = h % 360;
        return `hsl(${hue}, 70%, 40%)`;
    };

    calendarDisplayColor = (calendar, fallbackUuid = null) => {
        return this.calendarColor(boardTaskCalendarColorKey(calendar, fallbackUuid));
    };

    contextScopeType = () => {
        if (isGuid(this.props.caseUuid)) return "case";
        if (isGuid(this.props.owner)) return "person";
        return null;
    };

    contextScopeUuid = () => {
        return this.contextScopeType() === "case" ? this.props.caseUuid : this.props.owner;
    };

    hasContextScopeToggle = () => {
        return this.props.showContextScopeToggle && isGuid(this.contextScopeUuid());
    };

    contextScopeLabel = () => {
        const fallback = this.contextScopeType() === "case" ? "Case" : "Person";
        return String(this.props.contextScopeLabel || fallback).replace(/\s+/g, " ").trim() || fallback;
    };

    contextScopeOptions = () => ([
        { key: CALENDAR_SCOPE_MODES.ALL, label: "All Events" },
        { key: CALENDAR_SCOPE_MODES.CONTEXT, label: `${this.contextScopeLabel()} Only` },
    ]);

    setCalendarScopeMode = (mode) => {
        const nextMode = mode === CALENDAR_SCOPE_MODES.CONTEXT
            ? CALENDAR_SCOPE_MODES.CONTEXT
            : CALENDAR_SCOPE_MODES.ALL;

        this.setState({ calendarScopeMode: nextMode });
    };

    calendarFilterGroups = () => {
        return (this.state.calendars || [])
            .filter((calendar) => isGuid(calendar?.uuid) && !isBoardTasksCalendar(calendar))
            .map((calendar) => ({
                key: calendar.uuid,
                label: calendarLabel(calendar),
                calendar,
                uuids: [calendar.uuid],
            }));
    };

    boardTaskCalendarUuids = () => {
        return (this.state.calendars || [])
            .filter((calendar) => isGuid(calendar?.uuid) && isBoardTasksCalendar(calendar))
            .map((calendar) => calendar.uuid);
    };

    toggleCalendarVisible = (uuid) => {
        if (!isGuid(uuid)) return;

        this.toggleCalendarGroupVisible([uuid]);
    };

    toggleCalendarGroupVisible = (uuids) => {
        const targetUuids = (uuids || []).filter(isGuid);

        if (targetUuids.length === 0) return;

        this.setState((prev) => {
            const all = (prev.calendars || []).map((c) => c.uuid).filter(isGuid);

            const current =
                prev.visibleCalendarUuids === null || typeof prev.visibleCalendarUuids === "undefined"
                    ? all
                    : Array.isArray(prev.visibleCalendarUuids)
                        ? prev.visibleCalendarUuids
                        : [];

            const targetSet = new Set(targetUuids);
            const allTargetsVisible = targetUuids.every((uuid) => current.includes(uuid));
            const next = allTargetsVisible
                ? current.filter((x) => !targetSet.has(x))
                : [...new Set([...current, ...targetUuids])];

            return { ...prev, visibleCalendarUuids: next };
        });
    };

    setBoardTaskMode = (mode) => {
        const nextMode = Object.values(BOARD_TASK_MODES).includes(mode)
            ? mode
            : BOARD_TASK_MODES.MY;

        this.setState((prev) => {
            const all = (prev.calendars || []).map((c) => c.uuid).filter(isGuid);
            const boardTaskUuids = (prev.calendars || [])
                .filter((calendar) => isGuid(calendar?.uuid) && isBoardTasksCalendar(calendar))
                .map((calendar) => calendar.uuid);
            const boardTaskSet = new Set(boardTaskUuids);
            const current =
                prev.visibleCalendarUuids === null || typeof prev.visibleCalendarUuids === "undefined"
                    ? all
                    : Array.isArray(prev.visibleCalendarUuids)
                        ? prev.visibleCalendarUuids
                        : [];

            const withoutBoardTasks = current.filter((uuid) => !boardTaskSet.has(uuid));
            const visibleCalendarUuids = nextMode === BOARD_TASK_MODES.HIDE
                ? withoutBoardTasks
                : [...new Set([...withoutBoardTasks, ...boardTaskUuids])];

            return {
                ...prev,
                boardTaskMode: nextMode,
                visibleCalendarUuids,
            };
        });
    };

    showAllCalendars = () => {
        const all = (this.state.calendars || []).map((c) => c.uuid).filter(isGuid);
        this.setState((prev) => ({ ...prev, visibleCalendarUuids: all, boardTaskMode: BOARD_TASK_MODES.ALL }));
    };

    showNoCalendars = () => {
        this.setState((prev) => ({ ...prev, visibleCalendarUuids: [], boardTaskMode: BOARD_TASK_MODES.HIDE }));
    };

    ensureCalendarSelection = (calendars) => {
        const all = (calendars || []).map((c) => c.uuid).filter(isGuid);

        this.setState((prev) => {
            const current = prev.visibleCalendarUuids;

            if (current === null || typeof current === "undefined") {
                return { ...prev, visibleCalendarUuids: all };
            }

            if (Array.isArray(current) && current.length === 0) {
                return prev;
            }

            const pruned = (Array.isArray(current) ? current : []).filter((uuid) => all.includes(uuid));
            return { ...prev, visibleCalendarUuids: pruned };
        });
    };

    onEventDrop = async ({ event, start, end, allDay }) => {
        this.onPick(event, "event");

        if (event?.kind === "availability") {
            await this.props.saveAvailability({
                uuid: event.uuid,
                personUuid: event.personUuid,
                roleUuid: event.roleUuid,
                date: start.toISOString().slice(0, 10),
                startTime: start.toTimeString().slice(0, 8),
                endTime: end.toTimeString().slice(0, 8),
                meta: event.meta ?? null,
            });

            await this.loadAvailability();
            return;
        }

        await this.patchEventTime({ event, start, end, allDay });
    };

    onEventResize = async ({ event, start, end }) => {
        this.onPick(event, "event");

        if (event?.kind === "availability") {
            await this.props.saveAvailability({
                uuid: event.uuid,
                personUuid: event.personUuid,
                roleUuid: event.roleUuid,
                date: start.toISOString().slice(0, 10),
                startTime: start.toTimeString().slice(0, 8),
                endTime: end.toTimeString().slice(0, 8),
                meta: event.meta ?? null,
            });

            await this.loadAvailability();
            return;
        }

        await this.patchEventTime({ event, start, end, allDay: event?.allDay });
    };

    loadCalendars = async () => {
        let responses;

        if (this.props.firm) {
            responses = [await this.props.firmCalendars()];
        } else if (isGuid(this.props.owner)) {
            responses = [await this.props.listCalendars({ owner: this.props.owner, flags: "all" })];
        } else {
            // The mine endpoint includes calendars owned by or explicitly shared
            // with the current user. Public/shared calendars are exposed by the
            // separate available endpoint, so load both sets for the dashboard.
            responses = await Promise.all([
                this.props.myCalendars(),
                this.props.sharedCalendars(),
            ]);
        }

        const calendars = responses
            .flatMap((response) => response?.items ?? response?.calendars ?? response ?? [])
            .filter((calendar) => isGuid(calendar?.uuid));

        return [...new Map(calendars.map((calendar) => [calendar.uuid, calendar])).values()];
    };

    loadData = async (range, view = this.state.view) => {
        if (view === HISTORY_VIEW) {
            await this.loadHistoryData();
            return;
        }

        let { start, end } = range ?? this.state.visible;

        start = start instanceof Date ? start : new Date(start);
        end = end instanceof Date ? end : new Date(end);

        if (
            end.getHours() === 0 &&
            end.getMinutes() === 0 &&
            end.getSeconds() === 0 &&
            end.getMilliseconds() === 0
        ) {
            end = new Date(end.getTime() - 1);
        }

        const startAt = start.toISOString();
        const endAt = end.toISOString();

        try {
            const calendars = await this.loadCalendars();
            if (!this.__isMounted) return;

            const perCalendar = await Promise.all(
                calendars.map(async (cal) => {
                    const eventsResp = await this.props.listEvents({ startAt, endAt, calendar: cal.uuid });
                    const eventsRaw = eventsResp?.items ?? eventsResp?.events ?? eventsResp ?? [];

                    return eventsRaw.map((ev) => this.normalizeEvent(ev, cal));
                })
            );

            if (!this.__isMounted) return;

            const events = perCalendar.flat();

            this.setState(
                (prev) => ({
                    ...prev,
                    calendars,
                    events,
                    visible: { start, end },
                }),
                () => {
                    this.ensureCalendarSelection(calendars);
                }
            );
        } catch (err) {
            console.error("loadData failed:", err);
        }
    };

    loadHistoryData = async () => {
        const before = new Date().toISOString();
        const requestLimit = this.state.historyLimit + 1;

        this.setState({ historyLoading: true });

        try {
            const calendars = await this.loadCalendars();
            if (!this.__isMounted) return;

            const perCalendar = await Promise.all(
                calendars
                    .filter((cal) => isGuid(cal?.uuid))
                    .map(async (cal) => {
                        const eventsResp = await this.props.listEvents({
                            calendar: cal.uuid,
                            history: true,
                            before,
                            limit: requestLimit,
                            offset: 0,
                        });
                        const eventsRaw = eventsResp?.items ?? eventsResp?.events ?? eventsResp ?? [];
                        return eventsRaw.map((ev) => this.normalizeEvent(ev, cal));
                    })
            );

            if (!this.__isMounted) return;

            const sorted = perCalendar
                .flat()
                .filter((event) => event.start instanceof Date && !Number.isNaN(event.start.getTime()))
                .sort((a, b) => b.start - a.start);

            const events = sorted.slice(0, this.state.historyLimit);

            this.setState(
                (prev) => ({
                    ...prev,
                    calendars,
                    events,
                    historyHasMore: sorted.length > prev.historyLimit,
                    historyLoading: false,
                }),
                () => {
                    this.ensureCalendarSelection(calendars);
                }
            );
        } catch (err) {
            console.error("loadHistoryData failed:", err);
            if (this.__isMounted) this.setState({ historyLoading: false });
        }
    };

    onRangeChange = async (range, nextView) => {
        const visible = this.normalizeRange(range);
        const view = nextView || this.state.view;
        const enteringHistory = view === HISTORY_VIEW && this.state.view !== HISTORY_VIEW;

        await new Promise((resolve) => {
            this.setState((prev) => ({
                ...prev,
                visible,
                ...(nextView ? { view: nextView } : {}),
                ...(enteringHistory ? { historyLimit: HISTORY_INITIAL_LIMIT, historyHasMore: false } : {}),
            }), resolve);
        });

        await this.loadData(visible, view);

        if (this.state.showAvailability && view !== HISTORY_VIEW) {
            await this.loadAvailability(visible);
        }
    };

    onRangeSelect = (slotInfo) => {
        const start = slotInfo?.start;
        const end = slotInfo?.end;

        if (this.state.showAvailability) {
            this.openAvailabilityModal({ uuid: null, time: { start, end } });
            return;
        }

        this.openEventModal({ uuid: null, type: "Event", time: { start, end } });
    };

    onDrillDown = (date) => {
        this.setState((prev) => ({
            ...prev,
            date,
            view: Views.DAY,
        }));
    };

    onView = (view) => {
        this.setState((prev) => ({
            view,
            ...(view === HISTORY_VIEW && prev.view !== HISTORY_VIEW
                ? { historyLimit: HISTORY_INITIAL_LIMIT, historyHasMore: false }
                : {}),
        }));
    };

    onNavigate = (date, view) => {
        this.setState((prev) => ({
            ...prev,
            date,
            ...(view ? { view } : {}),
        }));
    };

    onHistoryLoadMore = async () => {
        await new Promise((resolve) => {
            this.setState((prev) => ({
                historyLimit: prev.historyLimit + HISTORY_STEP,
            }), resolve);
        });
        await this.loadHistoryData();
    };

    calendarViews = () => {
        const views = {
            [Views.MONTH]: true,
            [Views.WEEK]: true,
            [Views.WORK_WEEK]: true,
            [Views.DAY]: true,
            [Views.AGENDA]: true,
        };

        if (this.props.showHistoryView) views[HISTORY_VIEW] = HistoryView;

        return views;
    };

    renderToolbar = ({ label, localizer: toolbarLocalizer, onNavigate, onView, view, views }) => {
        const messages = toolbarLocalizer?.messages || {};
        const viewNames = Array.isArray(views)
            ? views
            : Object.keys(views || {}).filter((name) => !!views[name]);
        const createTypes = this.createTypes();

        return (
            <div className="rbc-toolbar matter-calendar-toolbar">
                <span className="rbc-btn-group matter-calendar-toolbar-nav">
                    <button type="button" onClick={() => onNavigate("TODAY")}>
                        {messages.today || "Today"}
                    </button>
                    <button type="button" onClick={() => onNavigate("PREV")}>
                        {messages.previous || "Back"}
                    </button>
                    <button type="button" onClick={() => onNavigate("NEXT")}>
                        {messages.next || "Next"}
                    </button>
                </span>

                {createTypes.length > 0 ? (
                    <span className="rbc-btn-group matter-calendar-toolbar-create">
                        <CondtionalDropdown
                            enabledVariant="outline-primary"
                            display
                            enabled
                            items={createTypes}
                            icon="plus"
                            buttonClassName="dropdown-toggle matter-calendar-create-button"
                        >
                            Create
                        </CondtionalDropdown>
                    </span>
                ) : null}

                <span className="rbc-toolbar-label">{label}</span>

                <span className="rbc-btn-group matter-calendar-toolbar-views">
                    {viewNames.length > 1
                        ? viewNames.map((name) => (
                            <button
                                type="button"
                                key={name}
                                className={view === name ? "rbc-active" : undefined}
                                onClick={() => onView(name)}
                            >
                                {messages[name] || name}
                            </button>
                        ))
                        : null}
                </span>
            </div>
        );
    };

    normalizeRange = (range) => {
        if (Array.isArray(range)) {
            if (!range.length) return { start: this.state.date, end: this.state.date };

            const start = range[0];
            const last = range[range.length - 1];

            const end = new Date(last);
            end.setHours(23, 59, 59, 999);

            return { start, end };
        }

        if (range?.start && range?.end) {
            const start = range.start;
            const end = new Date(range.end);

            if (
                end.getHours() === 0 &&
                end.getMinutes() === 0 &&
                end.getSeconds() === 0 &&
                end.getMilliseconds() === 0
            ) {
                end.setMilliseconds(end.getMilliseconds() - 1);
            }

            return { start, end };
        }

        return { start: this.state.date, end: this.state.date };
    };

    onPick = (selection, kind) => {
        this.setState((prev) => ({
            ...prev,
            selected: { kind, data: selection },
        }));
    };

    onSelectEvent = (event) => {
        this.onPick(event, "event");
        this.setState({ selectedEventUuid: event?.id ?? event?.uuid ?? null });
    };

    onSelectSlot = (slotInfo) => {
        if (slotInfo?.action === "select") {
            return this.onRangeSelect(slotInfo);
        }

        if (slotInfo?.action === "click") {
            this.setState((prev) => ({
                ...prev,
                scrollToTime: slotInfo.start,
            }));
            return this.onPick(slotInfo, "slot");
        }
    };

    eventPropGetter = (event) => {
        const selected = this.state.selected;
        const selectedData = selected?.data;
        const selectedId = selectedData?.id ?? selectedData?.uuid ?? null;
        const eventId = event?.id ?? event?.uuid ?? null;
        const isSelectedEvent = selected?.kind === "event" && selectedId === eventId;

        if (event?.kind === "availability") {
            return {
                style: {
                    backgroundColor: "rgba(34, 197, 94, 0.15)",
                    color: "#166534",
                    border: "1px dashed #166534",
                    borderRadius: "0px",
                    backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(22,101,52,0.10), rgba(22,101,52,0.10) 8px, transparent 8px, transparent 16px)",
                },
            };
        }

        const cu = event?.calendarUuid ?? event?.calendar?.uuid ?? null;
        const base = isGuid(cu)
            ? this.calendarDisplayColor(event?.calendar, cu)
            : "blue";

        return {
            style: {
                backgroundColor: isSelectedEvent ? "black" : base,
                color: "white",
                border: "none",
                borderRadius: "0px",
            },
        };
    };

    slotPropGetter = (date) => {
        const sel = this.state.selected;
        if (sel?.kind !== "slot") return {};

        const { start, end } = sel.data || {};
        if (!start) return {};

        const s = new Date(start);
        let e = end ? new Date(end) : null;

        if (!e || +e === +s) {
            e = new Date(+s + 30 * 60 * 1000);
        }

        const inRange = date >= s && date < e;
        return inRange ? { style: { backgroundColor: "rgba(0,0,0,0.08)" } } : {};
    };

    onDoubleClickEvent = (event) => {
        if (event?.kind === "availability") {
            return this.openAvailabilityModal({
                uuid: event.uuid,
                roleUuid: event.roleUuid,
                meta: event.meta,
                time: {
                    start: event.start,
                    end: event.end,
                },
            });
        }

        this.openEventModal({
            ...event,
            uuid: event?.id ?? event?.uuid ?? null,
            sourceUuid: event?.sourceUuid ?? null,
            type: event?.type ?? "Event",
            time: {
                start: event?.start ?? null,
                end: event?.end ?? null,
                allDay: !!event?.allDay,
            },
        });
    };

    onDoubleClickSlot = (slotInfo) => {
        const start = slotInfo?.start ?? null;
        const end = slotInfo?.end ?? null;

        if (this.state.showAvailability) {
            this.openAvailabilityModal({ uuid: null, time: { start, end } });
            return;
        }

        this.openEventModal({ uuid: null, type: "Event", time: { start, end } });
    };

    onConfirmDeleteModal = async () => {
        const kind = this.state.modal.confirm.kind ?? "event";
        const uuid = this.state.modal.confirm.uuid;

        if (kind === "availability") {
            await this.props.deleteAvailability({ uuid });
            this.setState(
                (prev) => ({
                    modal: {
                        ...prev.modal,
                        confirm: {
                            show: false,
                        },
                    },
                }),
                () => this.loadAvailability()
            );
            return;
        }

        await this.props.deleteEvent({ uuid });
        this.setState(
            (prev) => ({
                modal: {
                    ...prev.modal,
                    confirm: {
                        show: false,
                    },
                },
            }),
            () => this.loadData()
        );
    };

    onModalDelete = (e) => {
        const kind = e.kind ?? "event";

        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    show: false,
                },
                availability: {
                    show: false,
                },
                availabilityCopy: {
                    show: false,
                },
                confirm: {
                    title: kind === "availability" ? "Delete availability?" : "Delete calendar entry?",
                    body:
                        kind === "availability"
                            ? "Click ok to delete this availability window."
                            : "Click ok to delete this calendar entry.",
                    show: true,
                    uuid: e.uuid,
                    kind,
                    onCancel: this.onConfirmHideModal,
                    onOk: this.onConfirmDeleteModal,
                },
            },
        }));
    };

    onModalRemind = async (e) => {
        const data = await this.props.mappedEvent({ uuid: e.uuid });
        for (let i = 0; i < (data?.attendees?.length ?? 0); i++) {
            const attendee = data.attendees[i];
            await this.props.sendEvent({ data, email: attendee.email });
        }
    };

    onConfirmHideModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                confirm: {
                    show: false,
                },
            },
        }));
    };

    onModalClose = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    show: false,
                },
            },
        }));
    };

    onModalOk = async (e) => {
        this.onModalClose();

        const {
            status,
            style,
            eventType,
            title,
            description,
            location,
            type = "Event",
            startsAt: _startsAt,
            endsAt: _endsAt,
            allDay: _allDay,
            rrule,
            uuid,
            sourceUuid,
            calendarUuid,
            createdBy: createdByUuid,
            person: personUuid,
            matter: caseUuid,
            invites,
        } = e;

        let startsAt = _startsAt ?? e?.time?.start ?? null;
        let endsAt = _endsAt ?? e?.time?.end ?? null;
        let allDay = _allDay ?? e?.time?.allDay ?? false;

        const recurrence = rrule ?? e?.rrule ?? null;
        const fallbackCalendarUuid = this.state.calendars?.find((c) => isGuid(c?.uuid))?.uuid ?? null;

        const fields = {
            calendarUuid: isGuid(calendarUuid) ? calendarUuid : fallbackCalendarUuid,
            createdByUuid: isGuid(createdByUuid) ? createdByUuid : this.props.auth.user.uuid,
            status,
            style,
            eventType,
            title,
            type,
            description: normalizeCalendarDescription(description),
            location,
            startsAt,
            endsAt,
            allDay,
            recurrence,
        };

        if (!isGuid(fields.calendarUuid)) {
            console.warn("No calendarUuid available; cannot save event.");
            return;
        }

        if (isGuid(uuid)) fields.uuid = uuid;
        if (isGuid(sourceUuid)) fields.sourceUuid = sourceUuid;
        if (isGuid(personUuid)) fields.personUuid = personUuid;
        if (isGuid(caseUuid)) fields.caseUuid = caseUuid;

        if (startsAt && (!endsAt || +new Date(endsAt) === +new Date(startsAt))) {
            endsAt = new Date(+new Date(startsAt) + 30 * 60 * 1000);
            fields.endsAt = endsAt;
        }

        const shouldRemind = type !== "Block";

        if (Array.isArray(invites)) fields.invites = invites;

        const saved = await this.props.saveEvent(fields);
        const savedUuid = saved?.uuid ?? saved?.data?.uuid ?? null;

        if (shouldRemind && isGuid(savedUuid)) await this.onModalRemind({ uuid: savedUuid });

        await this.loadData();
    };

    onAddEventClick = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    ...prev.modal.event,
                    show: true,
                    type: "Event",
                },
            },
        }));
    };

    onAddTaskClick = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    ...prev.modal.event,
                    show: true,
                    type: "Task",
                },
            },
        }));
    };

    onAddBlockClick = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                event: {
                    ...prev.modal.event,
                    show: true,
                    type: "Block",
                },
            },
        }));
    };

    onAddAvailabilityClick = () => {
        const personUuid = this.state.availabilityPersonUuid || this.props.owner;
        if (!isGuid(personUuid)) return;

        const start = new Date();
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        this.openAvailabilityModal({ uuid: null, time: { start, end } });
    };

    createTypes = () => {
        const types = [
            {
                description: "create event",
                caption: "Event",
                onClick: this.onAddEventClick,
            },
            {
                description: "create task",
                caption: "Task",
                onClick: this.onAddTaskClick,
            },
            {
                description: "create block",
                caption: "Block",
                onClick: this.onAddBlockClick,
            },
        ];

        if (this.props.showAvailabilityControls) {
            types.push({
                description: "create availability",
                caption: "Availability",
                onClick: this.onAddAvailabilityClick,
            });
        }

        return types;
    };

    guidMatches = (value, target) => {
        return isGuid(value) && isGuid(target) && String(value).toLowerCase() === String(target).toLowerCase();
    };

    collectPersonUuids = (event) => {
        const task = event?.boardTask ?? event?.db?.boardTask ?? {};
        const attendees = [
            ...(Array.isArray(event?.attendees) ? event.attendees : []),
            ...(Array.isArray(event?.db?.attendees) ? event.db.attendees : []),
            ...(Array.isArray(event?.Attendees) ? event.Attendees : []),
        ];

        const values = [
            event?.personUuid,
            event?.db?.personUuid,
            event?.person?.uuid,
            event?.db?.person?.uuid,
            event?.Person?.uuid,
            event?.calendar?.personUuid,
            event?.db?.calendar?.personUuid,
            event?.Calendar?.personUuid,
            task?.personUuid,
            task?.assignedToUuid,
            task?.person?.uuid,
            task?.assignedTo?.uuid,
        ];

        attendees.forEach((attendee) => {
            values.push(
                attendee?.uuid,
                attendee?.personUuid,
                attendee?.key,
                attendee?.ownerKey,
                attendee?.person?.uuid,
                attendee?.Person?.uuid,
                attendee?.Attendee?.uuid,
                attendee?.Attendees?.Attendee?.uuid
            );
        });

        return values.filter(isGuid);
    };

    collectCaseUuids = (event) => {
        const task = event?.boardTask ?? event?.db?.boardTask ?? {};

        return [
            event?.caseUuid,
            event?.matterUuid,
            event?.db?.caseUuid,
            event?.db?.matterUuid,
            event?.case?.uuid,
            event?.matter?.uuid,
            event?.db?.case?.uuid,
            event?.Case?.uuid,
            task?.caseUuid,
            task?.matterUuid,
        ].filter(isGuid);
    };

    eventMatchesContextScope = (event) => {
        const targetUuid = this.contextScopeUuid();
        if (!isGuid(targetUuid)) return true;

        if (this.contextScopeType() === "case") {
            return this.collectCaseUuids(event).some((uuid) => this.guidMatches(uuid, targetUuid));
        }

        return this.collectPersonUuids(event).some((uuid) => this.guidMatches(uuid, targetUuid));
    };

    isEventVisible = (event, visibleSet) => {
        const cu = event?.calendarUuid ?? event?.calendar?.uuid ?? null;
        if (!visibleSet.size) return false;

        const calendarVisible = isGuid(cu) ? visibleSet.has(cu) : true;
        if (!calendarVisible) return false;

        if (isBoardTaskEvent(event) && this.state.boardTaskMode === BOARD_TASK_MODES.HIDE) return false;

        if (this.hasContextScopeToggle() && this.state.calendarScopeMode === CALENDAR_SCOPE_MODES.CONTEXT) {
            return this.eventMatchesContextScope(event);
        }

        return boardTaskEventMatchesMode(
            event,
            this.state.boardTaskMode,
            currentPersonUuid(this.props.auth)
        );
    };

    render() {
        const all = (this.state.calendars || []).map((c) => c.uuid).filter(isGuid);
        const boardTaskUuids = this.boardTaskCalendarUuids();
        const showBoardTaskControls = this.props.showBoardTaskControls !== false &&
            (boardTaskUuids.length > 0 || isGuid(this.props.owner));
        const showContextScopeToggle = this.hasContextScopeToggle();
        const contextScopeLabel = showContextScopeToggle ? this.contextScopeLabel() : "";

        const visibleUuids =
            this.state.visibleCalendarUuids === null || typeof this.state.visibleCalendarUuids === "undefined"
                ? all
                : Array.isArray(this.state.visibleCalendarUuids)
                    ? this.state.visibleCalendarUuids
                    : [];

        const visibleSet = new Set(visibleUuids);

        if (all.length === 0 && !this.props.owner) {
            return <div style={{ marginTop: "10px" }}>You don't have access to any calendars</div>;
        }

        const filteredEvents = (this.state.events || []).filter((ev) => this.isEventVisible(ev, visibleSet));

        const availabilityEvents = this.state.showAvailability ? (this.state.availability || []) : [];
        const isHistoryView = this.state.view === HISTORY_VIEW;
        const displayEvents = isHistoryView ? filteredEvents : [...filteredEvents, ...availabilityEvents];

        return (
            <>
                <div className="matter-calendar">
                    {this.props.showCalendarBar && (
                        <div className="matter-calendar-filter-bar">
                            <div className="matter-calendar-filter-divider" />

                            {showBoardTaskControls ? (
                                <div className="matter-calendar-filter-group">
                                    <span
                                        className="matter-calendar-color-dot"
                                        style={{ background: this.calendarColor("Board Tasks") }}
                                    />
                                    <span>Board Tasks</span>
                                    <SegmentedControl
                                        ariaLabel="Board task calendar mode"
                                        value={this.state.boardTaskMode}
                                        onChange={this.setBoardTaskMode}
                                        options={BOARD_TASK_MODE_OPTIONS}
                                    />
                                </div>
                            ) : null}

                            {this.calendarFilterGroups().map((group) => {
                                const effectiveVisible =
                                    this.state.visibleCalendarUuids === null
                                        ? all
                                        : this.state.visibleCalendarUuids ?? [];
                                const checked = group.uuids.every((uuid) => effectiveVisible.includes(uuid));
                                const partiallyChecked = !checked && group.uuids.some((uuid) => effectiveVisible.includes(uuid));
                                const color = this.calendarDisplayColor(group.calendar);

                                return (
                                    <label
                                        key={group.key}
                                        className="matter-calendar-filter-option"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            ref={(input) => {
                                                if (input) input.indeterminate = partiallyChecked;
                                            }}
                                            onChange={() => this.toggleCalendarGroupVisible(group.uuids)}
                                        />
                                        <span
                                            className="matter-calendar-color-dot"
                                            style={{ background: color }}
                                        />
                                        <span>{group.label}</span>
                                    </label>
                                );
                            })}

                            {showContextScopeToggle ? (
                                <div className="matter-calendar-filter-group matter-calendar-scope-filter">
                                    <SegmentedControl
                                        ariaLabel={`${contextScopeLabel} calendar scope`}
                                        className="matter-calendar-scope-toggle"
                                        value={this.state.calendarScopeMode}
                                        onChange={this.setCalendarScopeMode}
                                        options={this.contextScopeOptions()}
                                    />
                                </div>
                            ) : null}

                            {this.props.showAvailabilityControls ? (
                                <label className="matter-calendar-filter-option">
                                    <input
                                        type="checkbox"
                                        checked={!!this.state.showAvailability}
                                        onChange={() =>
                                            this.setState(
                                                (prev) => ({
                                                    showAvailability: !prev.showAvailability,
                                                    modal: {
                                                        ...prev.modal,
                                                        availabilityCopy: { show: false },
                                                    },
                                                }),
                                                async () => {
                                                    if (this.state.showAvailability) {
                                                        await this.loadMyRoles();
                                                        await this.loadAvailability();
                                                    } else {
                                                        this.setState({ availability: [] });
                                                    }
                                                }
                                            )
                                        }
                                    />
                                    <span
                                        className="matter-calendar-availability-dot"
                                    />
                                    <span>Availability</span>
                                </label>
                            ) : null}
                        </div>
                    )}

                    <div className="rounded-2xl shadow-sm ring-1 ring-gray-200 matter-calendar-frame">
                        <DnDCalendar
                            titleAccessor="title"
                            localizer={localizer}
                            events={displayEvents}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: "70vh" }}
                            date={this.state.date}
                            view={this.state.view}
                            defaultView={Views.WEEK}
                            views={this.calendarViews()}
                            messages={{ history: "History" }}
                            step={30}
                            timeslots={2}
                            scrollToTime={this.state.scrollToTime || new Date()}
                            popup
                            selectable
                            onSelectSlot={this.onSelectSlot}
                            onSelectEvent={this.onSelectEvent}
                            onView={this.onView}
                            onNavigate={this.onNavigate}
                            onRangeChange={this.onRangeChange}
                            eventPropGetter={this.eventPropGetter}
                            slotPropGetter={this.slotPropGetter}
                            onDrillDown={this.onDrillDown}
                            onDoubleClickEvent={this.onDoubleClickEvent}
                            onDoubleClickSlot={this.onDoubleClickSlot}
                            onEventDrop={this.onEventDrop}
                            onEventResize={this.onEventResize}
                            historyHasMore={this.state.historyHasMore}
                            historyLoading={this.state.historyLoading}
                            onHistoryLoadMore={this.onHistoryLoadMore}
                            components={{ toolbar: this.renderToolbar }}
                            resizable
                        />
                    </div>
                </div>

                <EventModal
                    {...this.state.modal.event}
                    calendars={this.state.calendars}
                    eventTypes={this.state.eventTypes}
                    eventNameTemplate={this.state.eventNameTemplate}
                    person={this.props.owner}
                    matter={this.props.caseUuid}
                    onOk={this.onModalOk}
                    onCancel={this.onModalClose}
                    onDelete={this.onModalDelete}
                    onRemind={this.onModalRemind}
                    size="xl"
                />

                <AvailabilityModal
                    {...this.state.modal.availability}
                    onOk={this.onAvailabilityOk}
                    onDelete={this.onModalDelete}
                    onCopy={this.onAvailabilityCopy}
                    onCancel={this.closeAvailabilityModal}
                    size="lg"
                />

                <AvailabilityCopyModal
                    {...this.state.modal.availabilityCopy}
                    onOk={this.onAvailabilityCopyOk}
                    onCancel={this.closeAvailabilityCopyModal}
                    size="md"
                />

                <ConfirmModal {...this.state.modal.confirm} />
            </>
        );
    }
}

Display.propTypes = {
    auth: PropTypes.object.isRequired,
    showCalendarBar: PropTypes.bool,
    showAvailabilityControls: PropTypes.bool,
    showHistoryView: PropTypes.bool,
    showContextScopeToggle: PropTypes.bool,
    contextScopeLabel: PropTypes.string,
    owner: PropTypes.string,
    caseUuid: PropTypes.string,
};

Display.defaultProps = {
    showCalendarBar: true,
    showAvailabilityControls: true,
    showHistoryView: false,
    showContextScopeToggle: false,
    contextScopeLabel: "",
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, {
    myCalendars,
    firmCalendars,
    listCalendars,
    sharedCalendars,
    saveEvent,
    listEvents,
    deleteEvent,
    sendEvent,
    mappedEvent,
    rolePeopleByPerson,
    myRolePeople,
    getAvailability,
    saveAvailability,
    deleteAvailability,
    copyAvailability,
    getSetting,
})(Display);
